<template>
    <div class="background-small">
        <section class="terms-container">
            <h1 class="terms-title">Terms and Conditions</h1>

            <div class="terms-content">
                <section v-for="(section, index) in termsSections" :key="index" class="terms-section">
                    <h2>{{ section.title }}</h2>
                    <p v-if="section.description">{{ section.description }}</p>
                    <ul v-if="section.items">
                        <li v-for="(item, itemIndex) in section.items" :key="itemIndex">{{ item }}</li>
                    </ul>
                    <div v-if="section.contact" class="contact-info">
                        <p v-for="(info, infoIndex) in section.contact" :key="infoIndex">{{ info }}</p>
                    </div>
                </section>
            </div>
        </section>
    </div>
</template>

<script setup>
const termsSections = [
    {
        title: "1. Acceptance of Terms",
        description: "By accessing and using SteamAnalyzer, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our service."
    },
    {
        title: "2. Description of Service",
        description: "SteamAnalyzer provides tools and services for Steam market users to:",
        items: [
            "Track item prices and market trends",
            "Monitor investments and portfolio performance",
            "Set up price alerts and notifications",
            "Access market analytics and insights",
            "Use P2P trading tools and features"
        ]
    },
    {
        title: "3. User Accounts",
        description: "To use our services, you must:",
        items: [
            "Provide accurate and complete information",
            "Maintain the security of your account",
            "Notify us immediately of any unauthorized use"
        ]
    },
    {
        title: "4. Subscription and Payments",
        description: "Our service offers both free and premium subscription options:",
        items: [
            "Free tier with basic features",
            "Premium plans with advanced features",
            "Automatic renewal unless cancelled",
            "Refund policy as per our guidelines"
        ]
    },
    {
        title: "5. User Conduct",
        description: "Users agree not to:",
        items: [
            "Violate any applicable laws or regulations",
            "Interfere with the service's operation",
            "Attempt to gain unauthorized access",
            "Use the service for any illegal purposes"
        ]
    },
    {
        title: "6. Intellectual Property",
        description: "All content, features, and functionality of SteamAnalyzer are owned by us and are protected by international copyright, trademark, and other intellectual property laws."
    },
    {
        title: "7. Limitation of Liability",
        description: "SteamAnalyzer is provided \"as is\" without any warranties. We are not liable for any damages arising from the use or inability to use our service."
    },
    {
        title: "8. Changes to Terms",
        description: "We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through our website."
    },
    {
        title: "9. Contact Information",
        description: "For any questions regarding these Terms and Conditions, please contact us at:",
        contact: [
            "Email: admin@steamanalyzer.com",
            "Last Updated: May 2025"
        ]
    }
];
</script>

<style scoped>
.terms-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.terms-title {
    font-size: 2.5rem;
    text-align: center;
    margin-bottom: 2rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.terms-content {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 2rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.terms-section {
    margin-bottom: 2.5rem;
    color: rgba(255, 255, 255, 0.9);
}

.terms-section:last-child {
    margin-bottom: 0;
}

.terms-section h2 {
    color: white;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
}

.terms-section p {
    line-height: 1.6;
    margin-bottom: 1rem;
}

.terms-section ul {
    list-style-type: none;
    padding-left: 1.5rem;
    margin-bottom: 1rem;
}

.terms-section ul li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
    line-height: 1.6;
}

.terms-section ul li::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #8650ac;
}

.contact-info {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 8px;
    margin-top: 1rem;
}

.contact-info p {
    margin-bottom: 0.5rem;
}

.contact-info p:last-child {
    margin-bottom: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
}

@media (max-width: 768px) {
    .terms-container {
        padding: 1rem;
    }

    .terms-title {
        font-size: 2rem;
        margin-bottom: 2rem;
    }

    .terms-content {
        padding: 1.5rem;
    }

    .terms-section h2 {
        font-size: 1.3rem;
    }
}
</style>